import { useEffect, useReducer, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import { useStyles } from "../../common/appStyles";
import { FormControl, InputLabel, MenuItem, Paper, Select, TextField } from '@mui/material';
import { ITenantDetail, IUpdateRegionRequest } from '../../types/regions';
import { GetTenantList } from '../../services/user-service';

const initialState = {
    id: 0,
    name: "",
    tenantId: 0
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        default:
            throw new Error();
    }
}

/**
 * @param props - all of the properties and functions Region Detail needs from the Region Admin Page.
 * @returns - a fullscreen dialog which allows a user to select a tenant and type in a region name,
 * or change an existing regions tenant/name. 
 * @remarks Region Detail Page is for editing or creating regions. It gets {@link ITenantDetail | tenants} 
 * from the API and uses them as the options for the drop down.
 */
export function RegionDetail(props) {
    const { showDialog, titleText, selectedRegion, handleClose, handleSave } = props;
    const id = selectedRegion.id;
    const classes = useStyles();
    const [tenants, setTenants] = useState<ITenantDetail[]>([]);

    const getTenantList = () => {
        GetTenantList()
            .then(response => {
                setTenants(response);
            })
            .catch((error) => {
                console.error(error);
            });
    }

    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        id: selectedRegion.id,
        name: selectedRegion.name,
        tenantId: selectedRegion.tenantId,
    });

    const { name, tenantId } = state;

    const setField = (field, value) => {
        dispatch({ type: 'SET_FIELD', field, value });
    }

    const handleSubmit = (event: any) => {
        event.preventDefault();
        const updatedRegion: IUpdateRegionRequest = { id: selectedRegion.id, name, tenantId }
        handleSave(updatedRegion);
    }

    useEffect(() => {
        getTenantList();
    }, []);

    const isFormValid =
        (
            tenantId !== (0) &&
            name !== ("")
        )

    return (
        <div>
            <Dialog
                open={showDialog}
                onClose={handleClose}
                maxWidth={"lg"}
                fullScreen
            >
                <AppBar sx={{ position: 'fixed' }}>
                    <Toolbar>
                        <Typography sx={{ flex: 1 }} variant="h5" component="div">
                            {titleText}
                        </Typography>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleClose}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <Grid className={classes.wrapper} alignItems={"center"}>
                    <Grid
                        container
                        rowSpacing={6}
                        className={classes.supWrapper}
                        direction={"column"}
                        justifyContent={'center'}
                        alignItems={"center"}

                    >
                        <Grid container direction={"row"} columnSpacing={4} alignItems={"center"} justifyContent={"center"}>
                            <Grid item xs={3} minWidth={{ xs: '40vh', md: '5vh' }} >
                                <Paper elevation={6}>
                                    <Grid
                                        container
                                        direction={"row"}
                                        justifyContent="center"
                                        alignItems="center"
                                        className={classes.gridPadding}
                                        rowSpacing={4}
                                    >
                                        <Grid item xs={12} md={12} className={classes.gridPaddingTop}>
                                            <Grid>
                                                <FormControl fullWidth>
                                                    <InputLabel>Tenant</InputLabel>
                                                    <Select
                                                        labelId="Tenant-label"
                                                        value={tenantId}
                                                        label="Tenant"
                                                        onChange={(event) => {
                                                            setField('tenantId', event.target.value);
                                                        }}
                                                        MenuProps={{
                                                            disableScrollLock: true,
                                                            PaperProps: {
                                                                style: {
                                                                    maxHeight: "20vh",
                                                                },
                                                            }
                                                        }}
                                                    >
                                                        {tenants.map((tenant) => (
                                                            <MenuItem key={tenant.id} value={tenant.id}>
                                                                {tenant.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} md={12} >
                                            <Grid className={classes.gridMargin}>
                                                <FormControl fullWidth>
                                                    <TextField value={name} onChange={(event) => setField('name', event.target.value)} label={"Region"} />
                                                </FormControl>
                                            </Grid>
                                        </Grid>

                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} alignSelf={"center"} className={classes.btnContainer} >
                            <Grid container direction={"column"} rowSpacing={3}>
                                <Grid item xs={7} md={7}>
                                    <Button onClick={handleSubmit} disabled={!isFormValid} variant="contained" fullWidth>
                                        {id > 0 ? "Update Region" : "Create Region"}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Dialog >
        </div >
    );
}